import { useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthContext";
import { useState, useEffect, useCallback } from "react";
import BoardItem from "../components/BoardItem/BoardItem";
import style from "../components/BoardItem/BoardItem.module.css";
import AddButton from "../components/AddButton/AddButton";
import BoardSettingsModal from "../components/BoardSettingsModal/BoardSettingsModal";
import Alert from "../components/Alert/Alert";
import BillingOverlays from "../components/BillingOverlays/BillingOverlays";
import { useTranslation } from 'react-i18next';
import { getDashboard, createBoard as apiCreateBoard, updateBoardStatus as apiUpdateBoardStatus } from "../api/boards";
import styles from "./Dashboard.module.css";

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [billingAction, setBillingAction] = useState(null);
  const [alert, setAlert] = useState({ show: false, text: "", type: "" });
  const { t } = useTranslation();
  const { updateIsCompanyAdmin } = useAuth();
  const navigate = useNavigate();

  const activeCompany = companies.find(c => c.id === activeCompanyId) || companies[0] || null;
  const isCompanyAdmin = activeCompany?.company_role === "admin";
  const dashboards = activeCompany?.dashboards || [];
  const activeBoards = dashboards.filter(b => b.status === "active");
  const archivedBoards = dashboards.filter(b => b.status === "archived");

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/login"); return; }

      const data = await getDashboard();
      setUser(data.user);
      setCompanies(data.companies || []);

      const anyAdmin = (data.companies || []).some(c => c.company_role === "admin");
      updateIsCompanyAdmin(anyAdmin);

      // Відновлюємо вибрану компанію з localStorage або беремо першу
      const saved = localStorage.getItem("active_company_id");
      const savedId = saved ? parseInt(saved) : null;
      const found = data.companies?.find(c => c.id === savedId);
      setActiveCompanyId(found ? found.id : data.companies?.[0]?.id || null);
    } catch (err) {
      if (err.status === 401) { navigate("/login"); return; }
      console.error("Помилка:", err);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!alert.show) return;
    const id = setTimeout(() => setAlert({ show: false, text: "", type: "" }), 5000);
    return () => clearTimeout(id);
  }, [alert.show]);

  const handleSelectCompany = (companyId) => {
    setActiveCompanyId(companyId);
    localStorage.setItem("active_company_id", companyId);
  };

  const handleCreateBoard = () => setBillingAction({ type: "create_board" });

  const createBoard = useCallback(async () => {
    if (!activeCompany) return;
    const data = await apiCreateBoard(activeCompany.slug);
    navigate(`/board/${data.company_slug}/${data.dashboard_slug}/edit`);
  }, [activeCompany, navigate]);

  const updateBoardStatus = useCallback(async (boardId, newStatus) => {
    await apiUpdateBoardStatus(boardId, newStatus);
    await fetchData();
    const msg = newStatus === "archived" ? t('dashboard.board_archived') :
                newStatus === "active"   ? t('dashboard.board_unarchived') :
                newStatus === "deleted"  ? t('dashboard.board_deleted') : t('dashboard.status_updated');
    setAlert({ show: true, text: msg, type: "success" });
  }, [fetchData]);

  const requestUpdateBoardStatus = useCallback((boardId, newStatus) => {
    if (newStatus === "active") {
      setBillingAction({ type: "update_board_status", boardId, newStatus });
      return;
    }
    (async () => {
      try {
        await updateBoardStatus(boardId, newStatus);
      } catch {
        setAlert({ show: true, text: t('dashboard.status_update_error'), type: "error" });
      }
    })();
  }, [updateBoardStatus]);

  const handleOpenSettings = (board) => {
    setSelectedBoard(board);
    setIsSettingsOpen(true);
  };

  return (
    <div>
      {alert.show && (
        <Alert text={alert.text} type={alert.type} onClose={() => setAlert({ show: false, text: "", type: "" })} />
      )}

      {/* Перемикач компаній — показується якщо юзер є в кількох компаніях */}
      {companies.length > 1 && (
        <div className={styles.companySwitcher}>
          {companies.map(company => (
            <button
              key={company.id}
              className={`${styles.companyTab} ${company.id === activeCompanyId ? styles.companyTabActive : ""}`}
              onClick={() => handleSelectCompany(company.id)}
            >
              {company.name}
              <span className={styles.companyRole}>
                {company.company_role === "admin" ? t('dashboard.role_admin') : t('dashboard.role_member')}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className={style.dashboardsContainer}>
        <h2 className="page-title">{t('dashboard.creation_title')}</h2>
        <div className={style.dashboardsHeader}>
          <h2 className="section-title">{t('dashboard.active_boards')} ({activeBoards.length})</h2>
          {isCompanyAdmin && (
            <div className={`button ${style.addBoardButton}`} onClick={handleCreateBoard}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.25 17V10.75H3V9.25H9.25V3H10.75V9.25H17V10.75H10.75V17H9.25Z" fill="white" />
              </svg>
              {t('dashboard.add_board')}
            </div>
          )}
        </div>

        <p className={`grey-text ${style.subtitle}`}>{t('dashboard.subtitle')}</p>

        <div className={style.cardsList}>
          {activeBoards.map(dashboard => (
            <BoardItem
              key={dashboard.id}
              dashboard={dashboard}
              initialCompanySlug={activeCompany?.slug}
              handleOpenSettings={handleOpenSettings}
              onStatusChange={fetchData}
              alert={alert}
              setAlert={setAlert}
              onUpdateStatus={requestUpdateBoardStatus}
              isAdmin={isCompanyAdmin || dashboard.board_role === "admin"}
            />
          ))}
          {isCompanyAdmin && <AddButton onClick={handleCreateBoard} title={t('dashboard.add_board')} fixedHeight={false} />}
        </div>
      </div>

      {archivedBoards.length > 0 && (
        <div className={style.dashboardsContainer}>
          <h2 className="section-title">{t('dashboard.archived_boards')} ({archivedBoards.length})</h2>
          <div className={style.cardsList}>
            {archivedBoards.map(dashboard => (
              <BoardItem
                key={dashboard.id}
                dashboard={dashboard}
                initialCompanySlug={activeCompany?.slug}
                handleOpenSettings={handleOpenSettings}
                onStatusChange={fetchData}
                isArchived={true}
                alert={alert}
                setAlert={setAlert}
                onUpdateStatus={requestUpdateBoardStatus}
                isAdmin={isCompanyAdmin || dashboard.board_role === "admin"}
              />
            ))}
          </div>
        </div>
      )}

      {isSettingsOpen && (
        <BoardSettingsModal board={selectedBoard} onClose={() => setIsSettingsOpen(false)} />
      )}

      {isCompanyAdmin && (
        <BillingOverlays
          company={activeCompany}
          activeBoards={activeBoards}
          archivedBoards={archivedBoards}
          onStatusChange={fetchData}
          billingAction={billingAction}
          clearBillingAction={() => setBillingAction(null)}
          createBoard={createBoard}
          updateBoardStatus={updateBoardStatus}
        />
      )}
    </div>
  );
};

export default Dashboard;
